/**
 * Click plugin: captures safe element metadata and frustration signals.
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

export function createClickPlugin(ctx: PluginContext) {
  const clickBuckets = new Map<string, { count: number; ts: number }>();

  const readMeta = (el: HTMLElement) => {
    const tag = el.tagName.toLowerCase();
    const id = el.getAttribute("id") || "";
    const className = el.getAttribute("class") || "";
    let cta =
      el.getAttribute("data-prospix-cta") || el.getAttribute("data-cta") || "";

    let hrefDomain: string | undefined;
    let contactChannel: "phone" | "email" | undefined;
    if (el instanceof HTMLAnchorElement && el.href) {
      try {
        const url = new URL(el.href);
        if (url.protocol === "tel:" || url.protocol === "mailto:") {
          // The destination belongs to the business, not the visitor. Never
          // retain a phone number, email, subject or message as activity data.
          contactChannel = url.protocol === "tel:" ? "phone" : "email";
          cta = `${contactChannel}_contact`;
        } else if (url.protocol === "http:" || url.protocol === "https:") {
          hrefDomain = url.hostname;
        }
      } catch {
        hrefDomain = undefined;
      }
    }

    return {
      tag,
      cta,
      hrefDomain,
      contactChannel,
      idHash: id ? ctx.redact.hashString(id) : undefined,
      classHash: className ? ctx.redact.hashString(className) : undefined,
    };
  };

  const deadClick = (meta: {
    tag: string;
    cta: string;
    hrefDomain?: string;
    idHash?: string;
    classHash?: string;
  }) => {
    const pathBefore = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const startedAt = Date.now();
    window.setTimeout(() => {
      if (!ctx.canTrackCategory("frustration_signals")) return;
      if (document.visibilityState === "hidden") return;
      const pathAfter = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (pathAfter !== pathBefore) return;
      ctx.track("dead_click", {
        tag: meta.tag,
        cta: meta.cta || undefined,
        href_domain: meta.hrefDomain,
        id_hash: meta.idHash,
        class_hash: meta.classHash,
        delay_ms: Date.now() - startedAt,
      });
    }, 1200);
  };

  const rageClick = (
    key: string,
    meta: { tag: string; cta: string; hrefDomain?: string },
  ) => {
    const now = Date.now();
    const previous = clickBuckets.get(key);
    if (!previous || now - previous.ts > 800) {
      clickBuckets.set(key, { count: 1, ts: now });
      return;
    }

    const next = { count: previous.count + 1, ts: now };
    clickBuckets.set(key, next);
    if (next.count < 3) return;

    ctx.track("rage_click", {
      tag: meta.tag,
      cta: meta.cta || undefined,
      href_domain: meta.hrefDomain,
      burst_count: next.count,
      window_ms: 800,
    });
    clickBuckets.set(key, { count: 0, ts: now });
  };

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const el = target.closest("a,button") as HTMLElement | null;
    if (!el) return;

    const meta = readMeta(el);

    if (ctx.canTrackCategory("behavioral_events")) {
      ctx.track("click", {
        tag: meta.tag,
        id_hash: meta.idHash,
        class_hash: meta.classHash,
        cta: meta.cta || undefined,
        href_domain: meta.hrefDomain,
      });

      if (meta.cta) {
        ctx.track("cta_click", {
          cta: meta.cta,
          tag: meta.tag,
          href_domain: meta.hrefDomain,
          ...(meta.contactChannel
            ? { contact_channel: meta.contactChannel, action_stage: "click" }
            : {}),
        });
      }
    }

    if (!ctx.canTrackCategory("frustration_signals")) return;

    const bucketKey = [
      meta.tag,
      meta.idHash ?? "",
      meta.classHash ?? "",
      meta.hrefDomain ?? "",
      meta.cta,
    ].join("|");
    rageClick(bucketKey, {
      tag: meta.tag,
      cta: meta.cta,
      hrefDomain: meta.hrefDomain,
    });
    deadClick(meta);
  };

  return {
    onInit() {
      document.addEventListener("click", handleClick, { capture: true });
    },
    onUnload() {
      document.removeEventListener("click", handleClick, true);
      clickBuckets.clear();
    },
  };
}

registerPlugin("click", createClickPlugin);
